import { useEffect, useState } from "react";
import type { SceneNodeInfo, SwitchNodeState } from "../../../../shared/api-contract";
import { useViewer } from "../../../../state/ViewerProvider";
import { useCaps } from "../../CapsProvider";
import { promptDialog } from "@/shared/ui";
import { Icon } from "../../../kit";
import { CompactCheck, CompactSlider, MiniBtn } from "../../inspector/rows";

/**
 * Switch Node (v22, iJewel) — switchable geometry: group several scene-tree
 * nodes so exactly one shows at a time (e.g. prong style A/B/C). Build one from
 * the current multi-selection; switch options at runtime.
 */
export function SwitchNodeControl() {
  const { client } = useViewer();
  const r = client.ring;
  const caps = useCaps();
  const [nodes, setNodes] = useState<SwitchNodeState[]>([]);
  const refresh = () => { r.listSwitchNodes().then(setNodes).catch(() => setNodes([])); };
  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [client]);

  // v31: rename + per-node camDistance / updateSceneOnSelectionChange.
  const rename = async (id: string, cur: string) => {
    const n = await promptDialog({ title: "Switch node title", label: "Title", defaultValue: cur });
    if (n == null || n === cur) return;
    void Promise.resolve(r.renameSwitchNode(id, n)).then(refresh);
  };
  const setConfig = (id: string, patch: { camDistance?: number; updateSceneOnSelectionChange?: boolean }) =>
    void Promise.resolve(r.setSwitchNodeConfig(id, patch)).then(refresh);

  const addFromSelection = async () => {
    const selGroups = await r.getSelection();
    if (selGroups.length < 2) { window.alert("Select 2+ parts (shift/ctrl-click) to group as switchable options."); return; }
    const tree = await r.getSceneTree();
    const nodeForGroup = (gid: string): string | null => {
      let hit: string | null = null;
      const walk = (n: SceneNodeInfo) => { if (!hit && n.groupId === gid) hit = n.id; n.children.forEach(walk); };
      tree.forEach(walk);
      return hit;
    };
    const nodeIds = selGroups.map(nodeForGroup).filter((x): x is string => !!x);
    if (nodeIds.length >= 2) { await r.addSwitchNode(`Switch ${nodes.length + 1}`, nodeIds); refresh(); }
  };

  // No plugin-wide flag exists in the contract; approximate webgi's master
  // "Enabled" toggle by flipping every switch node's own enabled flag at once.
  const allEnabled = nodes.length > 0 && nodes.every((n) => n.enabled);
  const toggleAll = (v: boolean) => void Promise.all(nodes.map((n) => r.setSwitchNodeEnabled(n.id, v))).then(refresh);

  return (
    <div className="flex flex-col gap-1.5 px-3 py-1.5">
      {nodes.length > 0 ? (
        <div className="flex items-center justify-between gap-2 px-0.5">
          <span className="text-[12px] text-ed-text-2">Enabled</span>
          <CompactCheck checked={allEnabled} onChange={toggleAll} />
        </div>
      ) : null}
      <div className="flex flex-wrap gap-1.5">
        <MiniBtn primary icon="account_tree" onClick={() => void addFromSelection()}>Add Node (from selection)</MiniBtn>
        <MiniBtn icon="refresh" onClick={refresh}>Refresh UI</MiniBtn>
      </div>
      {nodes.length === 0 ? (
        <span className="text-[11px] text-ed-text-3">Multi-select parts, then group them as a switch.</span>
      ) : (
        nodes.map((s) => (
          <div key={s.id} className="rounded border border-ed-border bg-white p-1.5">
            <div className="flex items-center justify-between gap-2">
              <span className="truncate text-[12px] font-medium text-ed-text">{s.name}</span>
              <div className="flex items-center gap-1.5">
                {caps.v31 ? (
                  <button className="text-ed-text-3 hover:text-ed-accent" title="Rename" onClick={() => rename(s.id, s.name)}>
                    <Icon name="edit" size={14} />
                  </button>
                ) : null}
                <CompactCheck checked={s.enabled} onChange={(v) => void r.setSwitchNodeEnabled(s.id, v).then(refresh)} />
                <button className="text-ed-text-3 hover:text-red-500" onClick={() => void Promise.resolve(r.removeSwitchNode(s.id)).then(refresh)}>✕</button>
              </div>
            </div>
            <div className="mt-1 flex flex-wrap gap-1">
              {s.options.map((o, i) => (
                <MiniBtn key={o.id} active={i === s.activeIndex} onClick={() => void r.selectSwitchOption(s.id, i).then(refresh)}>{o.name || `Option ${i + 1}`}</MiniBtn>
              ))}
            </div>
            {caps.v31 ? (
              <div className="mt-1.5 flex flex-col gap-1 border-t border-ed-border pt-1.5">
                <div className="flex items-center gap-1.5">
                  <span className="w-20 shrink-0 text-[11px] text-ed-text-2">Cam Distance</span>
                  <CompactSlider
                    value={s.camDistance ?? 1}
                    min={0.01}
                    max={2}
                    step={0.01}
                    onChange={(v) => setConfig(s.id, { camDistance: v })}
                  />
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[11px] text-ed-text-2">Update Scene On Selection Change</span>
                  <CompactCheck
                    checked={s.updateSceneOnSelectionChange ?? true}
                    onChange={(v) => setConfig(s.id, { updateSceneOnSelectionChange: v })}
                  />
                </div>
              </div>
            ) : null}
          </div>
        ))
      )}
    </div>
  );
}
